import UUID from 'util/UUID';
import { State } from 'reducers';
import { useParams } from 'util/reactRouterHooks';
import { useDispatch, useSelector } from 'react-redux';
import { getBuildingByUrlSlug, HasThemeId, HasUrlSlug } from 'selectors/buildingsSelectors';
import { ScheduleThemeForm } from '../scheduleThemeForm/ScheduleThemeForm';
import { Dialog } from 'ui/shared/containers';
import { AugmentedBuilding, DeviceId, Schedule } from 'shared/models';
import { useCallback, useState } from 'react';
import { scheduleThemeSuccess } from 'actions/themeActions';
import { callApi } from 'apiClient';
import { toError } from 'shared/util/toError';
import { useTranslation } from 'react-i18next';

require('./ScheduleThemeDialog.css');

export type Props = {
    visible: boolean;
    onClose(): void;
};

export const ScheduleThemeDialog = ({ visible, onClose }: Props) => {
    const { t } = useTranslation();

    const params = useParams<HasThemeId & HasUrlSlug>();
    const { themeId } = params;

    const building: AugmentedBuilding | null = useSelector((state: State) => getBuildingByUrlSlug(state, params));
    const buildingId = building && building.id;

    const [ saving, setSaving ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState('');

    const dispatch = useDispatch();

    const handleAccept = useCallback((deviceIds: Record<DeviceId, boolean>) => {
        // TODO: could eventually push the entire schedule instead of just the theme ID

        // deviceIds is a map of id => boolean that indicates if the theme
        // should be scheduled on that device. We're also using a false value
        // to indicate that the theme should be unscheduled from that device.
        const requestSchedules = Object.keys(deviceIds).map(deviceId => ({
            deviceId,
            // TODO: get the actual schedule for this device and remove the specified theme (if map value is false)
            // TODO: don't need to POST the schedule if the theme wasn't/isn't in the schedule
            rules: deviceIds[deviceId] ? [ {
                deviceId,
                themeId,
                properties: {
                    isDefault: true,
                },
            } ] : [],
        }));

        if (!buildingId) return;

        setSaving(true);
        setErrorMessage('');
        void (async () => {
            try {
                const schedules: Record<DeviceId, Schedule> = await callApi('PUT', `/api/buildings/${buildingId}/themes/${themeId}/schedule`, requestSchedules);
                dispatch(scheduleThemeSuccess(schedules, { buildingId, themeId }));
            }
            catch (e) {
                const err = toError(e);
                setErrorMessage(t([ `ERROR.${err.message}`, 'SERVER_ERROR' ]));
            }
            finally {
                setSaving(false);
            }
            onClose();
        })();
    }, [ buildingId, themeId, onClose, dispatch, t, setSaving, setErrorMessage ]);


    const handleCancel = () => {
        setErrorMessage('');
        onClose();
    };

    return (
        <Dialog
            id="schedule-theme-dialog"
            visible={ visible && !!themeId && !!buildingId }
            titleKey={ `SCHEDULE.THEME_DIALOG.TITLE` }
        >
            { buildingId &&
                <ScheduleThemeForm
                    buildingId={ buildingId }
                    themeId={ themeId }
                    saving={ saving }
                    errorMessage={ errorMessage }
                    onAccept={ handleAccept }
                    onCancel={ handleCancel }
                />
            }
        </Dialog>
    );
};
